/**
 * `PaymentDisplayManager` selects and renders the appropriate pricing display
 * components based on the current payment method.
 *
 * It coordinates rendering of:
 * - `SinglePaymentDisplay`
 * - `InstallmentPaymentDisplay`
 * - `DirectDebitTotalDisplay`
 * - `SubscriptionPaymentDisplay`
 *
 * When `isForMultipleDuration` is true, the output is adjusted for scenarios
 * where multiple duration options are shown together.
 */

import React from 'react';
import SinglePaymentDisplay from './SinglePaymentDisplay';
import InstallmentPaymentDisplay from './InstallmentPaymentDisplay';
import DirectDebitTotalDisplay from './DirectDebitTotalDisplay';
import SubscriptionPaymentDisplay from './SubscriptionPaymentDisplay';
import { PaymentMethod } from '../../../common/Constants';
import { InstallmentPrice } from '../../../common/ProductMethods';

export type PaymentDisplayManagerProps = {
  paymentMethod: string;
  priceMonthly: InstallmentPrice | undefined;
  priceSingle: number | undefined;
  priceSubscription: number | undefined;
  culture: string;
  isForMultipleDuration?: boolean;
};

const PaymentDisplayManager = ({
  paymentMethod,
  priceMonthly,
  priceSingle,
  priceSubscription,
  culture,
  isForMultipleDuration = false
}: PaymentDisplayManagerProps): JSX.Element => {
  const renderHighlightSinglePrice = () => {
    return (
      <>
        {priceSingle && (
          <SinglePaymentDisplay
            style={'p1-bold'}
            text={''}
            priceSingle={priceSingle}
            culture={culture}
          />
        )}

        {priceMonthly && !priceSingle && !isForMultipleDuration && (
          <DirectDebitTotalDisplay
            style={'p1-bold'}
            priceMonthly={priceMonthly}
            culture={culture}
          />
        )}

        {priceMonthly && !isForMultipleDuration && (
          <InstallmentPaymentDisplay
            style={''}
            text={''}
            priceMonthly={priceMonthly}
            culture={culture}
          />
        )}
      </>
    );
  };

  const renderHighlightDDPrice = () => {
    return (
      <>
        {priceMonthly && (
          <InstallmentPaymentDisplay
            style={'p1-bold'}
            text={''}
            priceMonthly={priceMonthly}
            culture={culture}
          />
        )}
        {priceMonthly && !priceSingle && (
          <DirectDebitTotalDisplay
            style={'label'}
            priceMonthly={priceMonthly}
            culture={culture}
          />
        )}
        {priceSingle && !isForMultipleDuration && (
          <SinglePaymentDisplay
            style={!priceMonthly ? 'p1-bold' : 'label'}
            text={!priceMonthly ? '' : 'or '}
            priceSingle={priceSingle}
            culture={culture}
          />
        )}
      </>
    );
  };

  const renderSubscriptionPrice = () => {
    if (!priceSubscription) {
      return <></>;
    }

    let price =
      priceMonthly && priceMonthly.firstInstallmentPrice < priceSubscription
        ? priceMonthly.firstInstallmentPrice
        : priceSubscription;

    price = isForMultipleDuration ? priceSubscription : price;

    return (
      <SubscriptionPaymentDisplay
        price={price}
        culture={culture}
        isForMultipleDuration={isForMultipleDuration}
      />
    );
  };

  switch (paymentMethod) {
    case PaymentMethod.Subscription:
      return renderSubscriptionPrice();
    case PaymentMethod.Single:
      return renderHighlightSinglePrice();
    default:
      return renderHighlightDDPrice();
  }
};

export default PaymentDisplayManager;
